// src/pages/admin/Building/ViewBooking.jsx
import React from 'react';
import {
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBuilding,
  FaInfoCircle,
  FaFileAlt,
  FaCreditCard,
} from 'react-icons/fa';

function ViewBooking({ data }) {
  if (!data) {
    return <div className="text-center py-4">No data available</div>;
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending', icon: <FaClock /> },
      'confirmed': { class: 'badge bg-info', label: 'Confirmed', icon: <FaCheckCircle /> },
      'checked_in': { class: 'badge bg-primary', label: 'Checked In', icon: <FaCheckCircle /> },
      'checked_out': { class: 'badge bg-success', label: 'Checked Out', icon: <FaCheckCircle /> },
      'cancelled': { class: 'badge bg-danger', label: 'Cancelled', icon: <FaTimesCircle /> },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>
      {statusInfo.icon} {statusInfo.label}
    </span>;
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending' },
      'partial': { class: 'badge bg-info', label: 'Partial' },
      'paid': { class: 'badge bg-success', label: 'Paid' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const displayFields = [
    // Booking Info
    { key: 'booking_id', label: 'Booking ID', icon: <FaFileAlt /> },
    { key: 'status', label: 'Booking Status', format: getStatusBadge },
    { key: 'payment_status', label: 'Payment Status', format: getPaymentStatusBadge },
    
    // Building Info
    { key: 'building_name', label: 'Building', icon: <FaBuilding /> },
    { key: 'building_address', label: 'Building Address', icon: <FaMapMarkerAlt /> },
    { key: 'building_city', label: 'City', icon: <FaMapMarkerAlt /> },
    { key: 'building_price', label: 'Price Per Day', format: formatPrice, icon: <FaMoneyBillWave /> },
    
    // User Info
    { key: 'full_name', label: 'Full Name', icon: <FaUser /> },
    { key: 'email', label: 'Email', icon: <FaEnvelope /> },
    { key: 'phone', label: 'Phone', icon: <FaPhone /> },
    { key: 'address', label: 'Address', icon: <FaMapMarkerAlt /> },
    { key: 'city', label: 'City', icon: <FaMapMarkerAlt /> },
    { key: 'state', label: 'State', icon: <FaMapMarkerAlt /> },
    { key: 'pincode', label: 'Pincode' },
    
    // Event Details
    { key: 'purpose', label: 'Purpose', icon: <FaInfoCircle /> },
    { key: 'event_type', label: 'Event Type' },
    { key: 'expected_guests', label: 'Expected Guests', icon: <FaUser /> },
    { key: 'special_requests', label: 'Special Requests', icon: <FaFileAlt /> },
    
    // Booking Dates
    { key: 'check_in_date', label: 'Check-In Date', format: formatDate, icon: <FaCalendarAlt /> },
    { key: 'check_out_date', label: 'Check-Out Date', format: formatDate, icon: <FaCalendarAlt /> },
    { key: 'total_days', label: 'Total Days', icon: <FaCalendarAlt /> },
    
    // Payment Details
    { key: 'total_amount', label: 'Total Amount', format: formatPrice, icon: <FaMoneyBillWave /> },
    { key: 'paid_amount', label: 'Paid Amount', format: formatPrice, icon: <FaMoneyBillWave /> },
    { key: 'balance_amount', label: 'Balance Amount', format: formatPrice, icon: <FaMoneyBillWave /> },
    { key: 'security_deposit', label: 'Security Deposit', format: formatPrice, icon: <FaMoneyBillWave /> },
    { key: 'payment_method', label: 'Payment Method', icon: <FaCreditCard /> },
    { key: 'transaction_id', label: 'Transaction ID', icon: <FaCreditCard /> },
    
    // Admin
    { key: 'admin_remarks', label: 'Admin Remarks', icon: <FaFileAlt /> },
    { key: 'created_at', label: 'Created At', format: formatDateTime, icon: <FaClock /> },
    { key: 'updated_at', label: 'Updated At', format: formatDateTime, icon: <FaClock /> },
  ];

  const getValue = (field) => {
    let value = data[field.key];
    if (field.format) {
      value = field.format(value);
    }
    return value || 'N/A';
  };

  return (
    <div className="view-booking-container">
      {/* Booking Header */}
      <div className="text-center mb-3">
        <h5 className="fw-bold text-primary">#{data.booking_id}</h5>
        <div className="d-flex justify-content-center gap-2 flex-wrap">
          {getStatusBadge(data.status)}
          {getPaymentStatusBadge(data.payment_status)}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <tbody>
            {displayFields.map((field) => (
              <tr key={field.key}>
                <th style={{ width: '30%', backgroundColor: '#f8f9fa' }}>
                  {field.icon && <span className="me-2">{field.icon}</span>}
                  {field.label}
                </th>
                <td>
                  {field.key === 'special_requests' || field.key === 'admin_remarks' ? (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {getValue(field)}
                    </div>
                  ) : (
                    getValue(field)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick Actions */}
      <div className="mt-3 d-flex gap-2 flex-wrap justify-content-center">
        {(data.status === 'pending' || data.status === 'confirmed') && (
          <>
            <button 
              className="btn btn-success btn-sm"
              onClick={() => {
                if (window.confirm('Confirm this booking?')) {
                  // Handle confirm
                }
              }}
            >
              <FaCheckCircle className="me-1" /> Confirm
            </button>
            <button 
              className="btn btn-danger btn-sm"
              onClick={() => {
                if (window.confirm('Cancel this booking?')) {
                  // Handle cancel
                }
              }}
            >
              <FaTimesCircle className="me-1" /> Cancel
            </button>
          </>
        )}
        {data.status === 'confirmed' && (
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (window.confirm('Mark as Checked In?')) {
                // Handle check-in
              }
            }}
          >
            <FaCheckCircle className="me-1" /> Check In
          </button>
        )}
        {data.status === 'checked_in' && (
          <button 
            className="btn btn-success btn-sm"
            onClick={() => {
              if (window.confirm('Mark as Checked Out?')) {
                // Handle check-out
              }
            }}
          >
            <FaCheckCircle className="me-1" /> Check Out
          </button>
        )}
      </div>
    </div>
  );
}

export default ViewBooking;